import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { useTranslation } from 'next-i18next/pages'
import { DoubleField } from '@/components/templates/Double/atoms/DoubleField'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  SimpleGrid,
  Flex,
  Center,
  Text,
  useMediaQuery,
  Button,
  Box,
} from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { Spinner } from '@/components/atoms/Spinner'
import { EXPLORERS, USDC_NEW_DENOM } from '@/constants/constants'
import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import { LAST_ROULETTE_GAME_BY_DENOM_DETAIL_QUERY } from '@/api/query'
import { tranformDoubleGameDetails } from '@/helpers/tranformDoubleGameDetails'
import { Game } from 'types/types'
import ShareIcon from '@/public/assets/icons/share.svg'
import { useState } from 'react'
import { ShareResultContent } from '../ShareResultModal/ShareResultModal'
import { BackButton } from '@/components/atoms/BackButton'
import Link from 'next/link'
import { getTokenByCurrency } from '@/services/token-service'
import { DoubleColor } from '@/__generated__/graphql'
import { SupportedCurrencies, TokenMoney } from '@ninjablaze/sdk-ts'

enum Steps {
  DETAILS,
  SHARING,
}

export default ({
  onClose,
  gameId,
  address,
  denom,
}: {
  onClose: () => void
  gameId: string
  address: string
  denom: string
}) => {
  const { t } = useTranslation()
  const [step, setStep] = useState(Steps.DETAILS)

  return (
    <Modal size={'lg'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          <Flex gap={2} alignItems={'center'}>
            {step === Steps.SHARING && (
              <BackButton callback={() => setStep(Steps.DETAILS)} styles={{ h: '30px', w: '30px', p: 1.5 }} />
            )}
            {t('common.game-details', { gameId })}
          </Flex>
        </ModalHeader>
        <ModalCloseButton onClick={onClose} size="sm" />
        <ModalBody pb={5}>
          {step === Steps.DETAILS && (
            <DoubleGameDetails
              denom={denom}
              switchTab={() => setStep(Steps.SHARING)}
              gameId={gameId}
              address={address}
            />
          )}
          {step === Steps.SHARING && <ShareResultContent game={Game.DOUBLE} gameId={gameId} />}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const DoubleGameDetails = ({
  gameId,
  denom,
  address,
  switchTab,
}: {
  gameId: string
  denom: string
  address: string
  switchTab: () => void
}) => {
  const { t } = useTranslation()
  const [isLargerThanMd] = useMediaQuery('(min-width: 768px)')
  const { data } = useQuery(LAST_ROULETTE_GAME_BY_DENOM_DETAIL_QUERY, {
    variables: { id: gameId || '', address: address || '', denom },
  })

  if (!data) {
    return (
      <Center>
        <Spinner />
      </Center>
    )
  }

  const gameDetails = tranformDoubleGameDetails(data)

  return (
    <>
      <SimpleGrid columns={{ base: 2, md: 3 }} mb={6} gap={3}>
        <Box
          p={'px'}
          rounded={'xl'}
          bg={
            gameDetails.winColor !== DoubleColor.Black
              ? 'ninja.500'
              : `linear-gradient(360deg, #5D52EE 0%, rgba(93, 82, 238, 0) 110.42%);`
          }
        >
          <Flex
            p={4}
            justifyContent={'center'}
            alignItems={'center'}
            rounded={'xl'}
            gap={3}
            flexDir={'column'}
            bg={'ninja.600'}
            bgGradient={
              gameDetails.winColor !== DoubleColor.Black
                ? 'none'
                : `linear-gradient(0deg, #5D52EE -3.82%, rgba(93, 82, 238, 0.26) 22.07%, rgba(93, 82, 238, 0) 49.94%);`
            }
          >
            <DoubleField
              boxSize={16}
              isDisabled={gameDetails.winColor !== DoubleColor.Black}
              type={DoubleColor.Black}
            />
            <FiatOrCryptoAmount
              wrapperStyles={{
                fontSize: 'sm',
              }}
              iconSize={'18px'}
              currencyStyles={{
                color: 'ninja.100',
              }}
              denom={denom}
              value={new TokenMoney(gameDetails.bets[DoubleColor.Black], denom)}
            />
          </Flex>
        </Box>
        <Box
          gridColumnStart={{ base: 1, md: 2 }}
          gridColumnEnd={{ base: 3, md: 3 }}
          order={{
            base: 2,
            md: 0,
          }}
          p={'px'}
          rounded={'xl'}
          bg={
            gameDetails.winColor !== DoubleColor.Green
              ? 'ninja.500'
              : `linear-gradient(360deg, #64E598 0%, rgba(100, 229, 152, 0) 110.42%);`
          }
        >
          <Flex
            p={4}
            justifyContent={{ base: 'start', md: 'center' }}
            alignItems={'center'}
            rounded={'xl'}
            gap={3}
            flexDir={{ base: 'row', md: 'column' }}
            bg={'ninja.600'}
            bgGradient={
              gameDetails.winColor !== DoubleColor.Green
                ? 'none'
                : `linear-gradient(0deg, #64E598 -3.82%, rgba(100, 229, 152, 0.26) 22.07%, rgba(100, 229, 152, 0) 49.94%);`
            }
          >
            <DoubleField
              boxSize={16}
              isDisabled={gameDetails.winColor !== DoubleColor.Green}
              type={DoubleColor.Green}
            />
            <FiatOrCryptoAmount
              wrapperStyles={{
                fontSize: 'sm',
              }}
              iconSize={'18px'}
              currencyStyles={{
                color: 'ninja.100',
              }}
              denom={denom}
              value={new TokenMoney(gameDetails.bets[DoubleColor.Green], denom)}
            />
          </Flex>
        </Box>
        <Box
          p={'px'}
          rounded={'xl'}
          bg={
            gameDetails.winColor !== DoubleColor.Red
              ? 'ninja.500'
              : `linear-gradient(360deg, #E080F9 0%, rgba(224, 128, 249, 0) 110.42%)`
          }
        >
          <Flex
            p={4}
            justifyContent={'center'}
            alignItems={'center'}
            rounded={'xl'}
            gap={3}
            flexDir={'column'}
            bg={'ninja.600'}
            bgGradient={
              gameDetails.winColor !== DoubleColor.Red
                ? 'none'
                : `linear-gradient(0deg, #E080F9 -3.82%, rgba(224, 128, 249, 0.26) 22.07%, rgba(224, 128, 249, 0) 49.94%);`
            }
          >
            <DoubleField boxSize={16} isDisabled={gameDetails.winColor !== DoubleColor.Red} type={DoubleColor.Red} />
            <FiatOrCryptoAmount
              wrapperStyles={{
                fontSize: 'sm',
              }}
              iconSize={'18px'}
              currencyStyles={{
                color: 'ninja.100',
              }}
              denom={denom}
              value={new TokenMoney(gameDetails.bets[DoubleColor.Red], denom)}
            />
          </Flex>
        </Box>
      </SimpleGrid>
      <SimpleGrid columnGap={3} columns={2}>
        <StatsRow name={t('common.game-status')} value={<Text color={'brand.green'}>{t('common.finished')}</Text>} />
        <StatsRow
          name={t('common.date')}
          value={<Text fontSize={{ base: 'xs', md: 'sm' }}>{gameDetails.created.toLocaleString()}</Text>}
        />
      </SimpleGrid>
      <SimpleGrid columnGap={3} columns={2}>
        <StatsRow
          name={t('common.start-block')}
          value={
            <CopiableLink
              trimmedLength={16}
              maxLength={32}
              link={`${EXPLORERS.INJECTIVE_BLOCKS}${gameDetails.startBlock}/`}
              value={gameDetails.startBlock}
            />
          }
        />
        {gameDetails.jackpotAmount !== '0' ? (
          <StatsRow
            name={t('common.jackpot')}
            value={
              <FiatOrCryptoAmount
                amountStyles={{
                  color: 'brand.green',
                }}
                currencyStyles={{
                  color: 'ninja.200',
                }}
                iconSize={'18px'}
                denom={USDC_NEW_DENOM}
                value={new TokenMoney(gameDetails.jackpotAmount, getTokenByCurrency(SupportedCurrencies.USDC).denom)}
              />
            }
          />
        ) : (
          <StatsRow
            name={t('common.randomness')}
            value={
              <CopiableLink trimmedLength={isLargerThanMd ? 16 : 8} maxLength={32} value={gameDetails.randomness} />
            }
          />
        )}
      </SimpleGrid>
      {gameDetails.jackpotAmount !== '0' && (
        <StatsRow
          name={t('common.randomness')}
          value={
            <CopiableLink trimmedLength={24} maxLength={isLargerThanMd ? 48 : 32} value={gameDetails.randomness} />
          }
        />
      )}
      <SimpleGrid columns={2} gap={3} mt={1}>
        <Button variant={'orange'} rounded={'xl'} gap={2.5} onClick={switchTab}>
          {t('common.share')} <ShareIcon />
        </Button>
        <Link href={'/double/history/' + gameId}>
          <Button w={'full'} rounded={'xl'}>
            {t('common.more-details')}
          </Button>
        </Link>
      </SimpleGrid>
    </>
  )
}
